import React, { useState, useCallback, useEffect } from 'react';
import { LoadingOverlay } from './LoadingOverlay';
import { generateSeatId, getRowLabel } from '../utils/seatNaming';
import { useSeating } from '../hooks/useSeating';
import { api } from '../services/api';
import { toast } from '../utils/swal';
import AppHeader from './AppHeader';
import './ManualReservation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PAYMENT_METHODS = [
  { value: 0, label: 'Nakit',          icon: 'fa-money-bill-wave' },
  { value: 1, label: 'Kredi Kartı',    icon: 'fa-credit-card' },
  { value: 2, label: 'Havale/EFT',     icon: 'fa-building-columns' },
  { value: 3, label: 'Online Ödeme',   icon: 'fa-globe' },
  { value: 4, label: 'Ücretsiz',       icon: 'fa-gift' },
];

const TICKET_TYPES = [
  { value: 0, label: 'Tam Bilet',  icon: 'fa-user',         color: '#4CAF50' },
  { value: 1, label: 'Öğrenci',   icon: 'fa-graduation-cap', color: '#2196F3' },
  { value: 2, label: 'Davetli',   icon: 'fa-envelope-open-text', color: '#9C27B0' },
];

const ManualReservation = ({
  event, venue, onBack, onComplete,
  onGoToProfile, onGoToEventManagement, onGoToAdminPanel, onGoToVenueManage, onGoToQrScanner,
}) => {
  const [selectedSeats, setSelectedSeats]     = useState({});
  const [blockedSeatsMap, setBlockedSeatsMap] = useState({});
  const [loading, setLoading]                 = useState(false);

  // Müşteri formu
  const [customerName,  setCustomerName]  = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes,         setNotes]         = useState('');

  // Ödeme
  const [paymentMethod,   setPaymentMethod]   = useState(0);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [paidAmount,      setPaidAmount]      = useState(0);

  // Bilet tipi modal
  const [showTypeModal,    setShowTypeModal]    = useState(false);
  const [pendingSeatId,    setPendingSeatId]    = useState(null);
  const [pendingSection,   setPendingSection]   = useState(null);

  const headerNavProps = {
    onGoToProfile, onGoToEventManagement, onGoToAdminPanel, onGoToVenueManage, onGoToQrScanner,
  };

  useEffect(() => {
    api.getBlockedSeats(event.id)
      .then(data => {
        const map = {};
        (data || []).forEach(s => { map[s.seatId] = s.type; });
        setBlockedSeatsMap(map);
      })
      .catch(() => {});
  }, [event.id]);

  const handleExternalRelease = useCallback((seatId) => {
    setSelectedSeats(prev => {
      const next = { ...prev };
      delete next[seatId];
      return next;
    });
  }, []);

  const { seats, mySeats, selectSeat, releaseSeat, isConnected } = useSeating(event.id, handleExternalRelease);

  // ── Koltuk tıklama ──
  const handleSeatClick = async (seatId) => {
    if (!isConnected) { toast.warning('Bağlantı kuruluyor, lütfen bekleyin...'); return; }

    // Zaten seçiliyse kaldır
    if (mySeats.has(seatId)) {
      await releaseSeat(seatId);
      setSelectedSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      return;
    }

    const section = findSection(seatId);
    if (!section) { toast.error('Koltuk bilgisi hatalı!'); return; }

    // Bilet tipi seç
    setPendingSeatId(seatId);
    setPendingSection(section);
    setShowTypeModal(true);
  };

  const findSection = (seatId) => {
    return venue.sections.find(s => {
      if (s.namingMode === 0 || !s.namingMode) {
        const seatLetter = seatId.match(/^[A-Z]+/)?.[0];
        if (!seatLetter) return false;
        let rowNum = 0;
        for (let i = 0; i < seatLetter.length; i++) {
          rowNum = rowNum * 26 + (seatLetter.charCodeAt(i) - 64);
        }
        return rowNum >= s.startRow && rowNum <= s.endRow;
      }
      return seatId.startsWith(s.rowPrefix);
    });
  };

  const handleTypeSelect = async (ticketTypeValue) => {
    setShowTypeModal(false);
    if (!pendingSeatId || !pendingSection) return;

    const seatId   = pendingSeatId;
    const section  = pendingSection;
    setPendingSeatId(null);
    setPendingSection(null);

    const success = await selectSeat(seatId);
    if (!success) { toast.error(`${seatId} koltuğu başkası tarafından seçildi!`); return; }

    // Fiyat hesapla — manuel satışta admin override edebilir, base fiyatı koyuyoruz
    const basePrice = ticketTypeValue === 1
      ? (event.sectionStudentPricing?.[section.id] || event.sectionPricing?.[section.id] || 0)
      : (event.sectionPricing?.[section.id] || 0);

    let finalPrice = basePrice;
    try {
      const priceData = await api.calculatePrice({
        eventId:    event.id,
        sectionId:  section.id,
        basePrice:  basePrice,
        ticketType: ticketTypeValue,
      });
      finalPrice = priceData?.finalPrice ?? basePrice;
    } catch { /* fiyat API'si çalışmazsa base kullan */ }

    setSelectedSeats(prev => ({
      ...prev,
      [seatId]: {
        seatId,
        sectionName: section.name,
        sectionId:   section.id,
        ticketType:  ticketTypeValue,
        basePrice,
        finalPrice,
      }
    }));

    // Toplam tutarı otomatik güncelle
    setPaidAmount(prev => {
      const currentTotal = Object.values(selectedSeats).reduce((s, x) => s + x.finalPrice, 0);
      return currentTotal + finalPrice;
    });
  };

  const handleRemoveSeat = async (seatId) => {
    await releaseSeat(seatId);
    setSelectedSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
  };

  // Bilet fiyatı override
  const handlePriceOverride = (seatId, newPrice) => {
    setSelectedSeats(prev => ({
      ...prev,
      [seatId]: { ...prev[seatId], finalPrice: parseFloat(newPrice) || 0 }
    }));
  };

  // Bilet tipi değiştir (sidebar'dan)
  const handleTypeChange = (seatId, newType) => {
    setSelectedSeats(prev => {
      const seat    = prev[seatId];
      const section = venue.sections.find(s => s.name === seat.sectionName);
      const basePrice = newType === 1
        ? (event.sectionStudentPricing?.[section?.id] || seat.basePrice)
        : (event.sectionPricing?.[section?.id] || seat.basePrice);
      return {
        ...prev,
        [seatId]: { ...seat, ticketType: newType, basePrice, finalPrice: basePrice }
      };
    });
  };

  const getTotalPrice = () =>
    Object.values(selectedSeats).reduce((s, x) => s + (x.finalPrice || 0), 0);

  const getSeatStatus = (seatId) => {
    if (blockedSeatsMap[seatId] === 'protocol') return 'blocked-protocol';
    if (blockedSeatsMap[seatId] === 'guest')    return 'blocked-guest';
    const seatInfo = seats[seatId];
    if (!seatInfo) return 'available';
    if (mySeats.has(seatId)) {
      const t = selectedSeats[seatId]?.ticketType;
      if (t === 0) return 'my-full';
      if (t === 1) return 'my-student';
      return 'my-temp';
    }
    return 'occupied';
  };

  const handleSubmit = async () => {
    if (!customerName.trim())  { toast.warning('Ad Soyad zorunludur!');       return; }
    if (!customerEmail.trim()) { toast.warning('Email zorunludur!');           return; }
    if (!customerPhone.trim()) { toast.warning('Telefon zorunludur!');         return; }
    if (Object.keys(selectedSeats).length === 0) { toast.warning('En az bir koltuk seçin!'); return; }

    setLoading(true);
    try {
      const reservationData = {
        eventId:         event.id,
        venueId:         event.venueId,
        eventName:       event.name,
        eventDate:       event.date,
        seats: Object.values(selectedSeats).map(s => ({
          seatId:      s.seatId,
          sectionName: s.sectionName,
          ticketType:  s.ticketType,
          basePrice:   s.basePrice,
          finalPrice:  s.finalPrice,
        })),
        customerName,
        customerEmail,
        customerPhone,
        totalAmount:     getTotalPrice(),
        paidAmount:      paymentReceived ? parseFloat(paidAmount) || 0 : 0,
        paymentReceived,
        paymentMethod:   parseInt(paymentMethod),
        status:          1, // Confirmed
        isManual:        true,
        notes,
      };

      const reservation = await api.createReservation(reservationData);
      if (!reservation) throw new Error('Rezervasyon oluşturulamadı');

      toast.success('Manuel rezervasyon oluşturuldu!');
      onComplete?.(reservation);
    } catch (err) {
      console.error(err);
      toast.error('Rezervasyon oluşturulamadı!');
    } finally {
      setLoading(false);
    }
  };

  // ── Koltuk haritası render ──
  const renderSeatMap = () => {
    const hasPositions = venue.sections.some(s => s.position?.x !== undefined);

    const SEAT_W = 30, SEAT_H = 30, ROW_LABEL_W = 36, SECTION_PADDING = 12, SECTION_HEADER_H = 52, GAP = 16;

    const getSectionRenderSize = (s) => {
      const rowCount = s.endRow - s.startRow + 1;
      const w = ROW_LABEL_W + s.seatsPerRow * (SEAT_W + 4) + SECTION_PADDING * 2;
      const h = SECTION_HEADER_H + rowCount * (SEAT_H + 4) + SECTION_PADDING * 2;
      return { w, h };
    };

    const renderSection = (section) => {
      const totalRows = section.endRow - section.startRow + 1;
      const sectionPrice = event.sectionPricing?.[section.id] || 0;
      return (
        <div key={section.id} className="section">
          <div className="section-header">
            <h3>{section.name}</h3>
            <span className="section-price">₺{sectionPrice}</span>
          </div>
          <div className="seats-grid">
            {Array.from({ length: totalRows }, (_, rowIndex) => {
              const seatsInRow = section.rowSeats?.[rowIndex] || section.seatsPerRow;
              return (
                <div key={rowIndex} className="seat-row">
                  <span className="row-label">{getRowLabel(section, rowIndex)}</span>
                  {Array.from({ length: seatsInRow }, (_, seatIndex) => {
                    const seatId  = generateSeatId(section, rowIndex, seatIndex);
                    const status  = getSeatStatus(seatId);
                    const blocked = ['blocked', 'blocked-protocol', 'blocked-guest'].includes(status);
                    const occupied = (seats[seatId] && !mySeats.has(seatId)) || blocked;
                    return (
                      <button
                        key={seatIndex}
                        className={`seat ${status}`}
                        onClick={() => !blocked && handleSeatClick(seatId)}
                        disabled={occupied || !isConnected}
                        title={
                          status === 'blocked-protocol' ? `${seatId} - Protokol` :
                          status === 'blocked-guest'    ? `${seatId} - Davetli`  :
                          blocked                       ? `${seatId} - Blokeli`  :
                          `${seatId} - ₺${sectionPrice}`
                        }
                      >
                        {section.reverseSeats
                          ? (section.seatStartNumber ?? 1) + seatsInRow - 1 - seatIndex
                          : (section.seatStartNumber ?? 1) + seatIndex}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    if (hasPositions) {
      const ROW_THRESHOLD = 50;
      const sorted = [...venue.sections].sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));
      const rows = [];
      sorted.forEach(s => {
        const sy = s.position?.y || 0;
        const lastRow = rows[rows.length - 1];
        if (lastRow && Math.abs((lastRow[0].position?.y || 0) - sy) < ROW_THRESHOLD) lastRow.push(s);
        else rows.push([s]);
      });
      rows.forEach(row => row.sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0)));

      const sectionPositions = new Map();
      let currentTop = 0;
      rows.forEach(row => {
        let currentLeft = 0;
        const rowHeight = Math.max(...row.map(s => getSectionRenderSize(s).h));
        row.forEach(s => {
          const { w } = getSectionRenderSize(s);
          sectionPositions.set(s.id, { left: currentLeft, top: currentTop });
          currentLeft += w + GAP;
        });
        currentTop += rowHeight + GAP;
      });

      const positions = venue.sections.map(s => {
        const { left, top } = sectionPositions.get(s.id) || { left: 0, top: 0 };
        const { w, h } = getSectionRenderSize(s);
        return { left, top, right: left + w, bottom: top + h };
      });
      const canvasW = Math.max(...positions.map(p => p.right)) + 40;
      const canvasH = Math.max(...positions.map(p => p.bottom)) + 40;

      return (
        <div className="venue-layout-2d-scroll">
          <div className="venue-layout-2d" style={{ width: canvasW, height: canvasH }}>
            {venue.sections.map(section => {
              const { left, top } = sectionPositions.get(section.id) || { left: 0, top: 0 };
              return (
                <div key={section.id} className="section section-2d" style={{ left, top }}>
                  {renderSection(section).props.children}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="venue-layout">
        {[...venue.sections]
          .sort((a, b) => (a.position?.y || 999) - (b.position?.y || 999))
          .map(section => renderSection(section))}
      </div>
    );
  };

  const seatCount = Object.keys(selectedSeats).length;
  const totalPrice = getTotalPrice();

  return (
    <div className="manual-reservation">
      {loading && <LoadingOverlay message="Rezervasyon oluşturuluyor..." />}

      <AppHeader
        title="Manuel Rezervasyon"
        onBack={onBack}
        {...headerNavProps}
      />

      <div className="mr-layout">

        {/* ── SOL: Koltuk Haritası ── */}
        <div className="mr-map-panel">
          <div className="mr-map-header">
            <div className="mr-event-info">
              {(event.mainPhoto || event.venueMainPhoto) && (
                <img
                  src={`${API_URL}${event.mainPhoto || event.venueMainPhoto}`}
                  alt={event.name}
                  className="mr-event-thumb"
                />
              )}
              <div>
                <h3>{event.name}</h3>
                <p>
                  <i className="fa-solid fa-location-dot" /> {venue.name}
                  &nbsp;·&nbsp;
                  <i className="fa-solid fa-calendar" />{' '}
                  {new Date(event.date).toLocaleDateString('tr-TR', {
                    day: 'numeric', month: 'long', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            <div className={`mr-connection ${isConnected ? 'connected' : 'disconnected'}`}>
              <i className={`fa-solid fa-circle fa-xs`} />
              {isConnected ? ' Bağlı' : ' Bağlantı Yok'}
            </div>
          </div>

          {/* Lejant */}
          <div className="legend">
            <div><span className="seat-legend available" /> Boş</div>
            <div><span className="seat-legend my-temp" /> Seçildi</div>
            <div><span className="seat-legend occupied" /> Dolu</div>
            <div><span className="seat-legend blocked-protocol" /> Protokol</div>
            <div><span className="seat-legend blocked-guest" /> Davetli</div>
          </div>

          <div className="stage"><i className="fa-solid fa-clapperboard" /> SAHNE</div>

          {renderSeatMap()}
        </div>

        {/* ── SAĞ: Sidebar ── */}
        <div className="mr-sidebar">

          {/* Seçili Koltuklar */}
          <div className="mr-section">
            <h4><i className="fa-solid fa-chair" /> Seçili Koltuklar {seatCount > 0 && `(${seatCount})`}</h4>
            {seatCount === 0 ? (
              <div className="mr-empty-seats">
                <i className="fa-solid fa-hand-pointer" /> Haritadan koltuk seçin
              </div>
            ) : (
              <div className="mr-seats-list">
                {Object.values(selectedSeats).map(seat => {
                  const tt = TICKET_TYPES.find(t => t.value === seat.ticketType);
                  return (
                    <div key={seat.seatId} className="mr-seat-card">
                      <div className="mr-seat-top">
                        <span className="mr-seat-id">
                          <i className="fa-solid fa-chair fa-xs" /> {seat.seatId}
                        </span>
                        <button
                          className="mr-seat-remove"
                          onClick={() => handleRemoveSeat(seat.seatId)}
                          title="Kaldır"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                      <div className="mr-seat-section">{seat.sectionName}</div>
                      <div className="mr-seat-controls">
                        <select
                          value={seat.ticketType}
                          onChange={e => handleTypeChange(seat.seatId, parseInt(e.target.value))}
                          className="mr-type-select"
                          style={{ borderColor: tt?.color }}
                        >
                          {TICKET_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        <div className="mr-price-input">
                          <span>₺</span>
                          <input
                            type="number"
                            value={seat.finalPrice}
                            onChange={e => handlePriceOverride(seat.seatId, e.target.value)}
                            min="0"
                            step="1"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="mr-total">
                  <span>Toplam:</span>
                  <strong>₺{totalPrice.toFixed(2)}</strong>
                </div>
              </div>
            )}
          </div>

          {/* Müşteri Bilgileri */}
          <div className="mr-section">
            <h4><i className="fa-solid fa-user" /> Müşteri Bilgileri</h4>
            <div className="mr-form">
              <div className="mr-field">
                <label>Ad Soyad *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Ahmet Yılmaz"
                />
              </div>
              <div className="mr-field">
                <label>Email *</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)}
                  placeholder="ornek@email.com"
                />
              </div>
              <div className="mr-field">
                <label>Telefon *</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="5551234567"
                  maxLength="10"
                />
              </div>
              <div className="mr-field">
                <label>Notlar</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="İsteğe bağlı not..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Ödeme */}
          <div className="mr-section">
            <h4><i className="fa-solid fa-credit-card" /> Ödeme</h4>
            <div className="mr-form">
              <div className="mr-field">
                <label>Ödeme Yöntemi</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mr-field mr-checkbox-field">
                <label className="mr-checkbox-label">
                  <input
                    type="checkbox"
                    checked={paymentReceived}
                    onChange={e => {
                      setPaymentReceived(e.target.checked);
                      if (e.target.checked) setPaidAmount(totalPrice);
                      else setPaidAmount(0);
                    }}
                  />
                  <span>Ödeme Alındı</span>
                </label>
              </div>
              {paymentReceived && (
                <div className="mr-field">
                  <label>Alınan Tutar (₺)</label>
                  <input
                    type="number"
                    value={paidAmount}
                    onChange={e => setPaidAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Kaydet */}
          <button
            className="mr-submit-btn"
            onClick={handleSubmit}
            disabled={loading || seatCount === 0}
          >
            {loading
              ? <><i className="fa-solid fa-spinner fa-spin" /> Kaydediliyor...</>
              : <><i className="fa-solid fa-floppy-disk" /> Rezervasyonu Kaydet ({seatCount} Koltuk)</>}
          </button>

        </div>
      </div>

      {/* Bilet tipi seçim modal */}
      {showTypeModal && (
        <div className="modal-overlay" onClick={() => { setShowTypeModal(false); setPendingSeatId(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fa-solid fa-ticket" /> Bilet Tipi Seçin</h3>
              <button className="modal-close" onClick={() => { setShowTypeModal(false); setPendingSeatId(null); }}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <p className="modal-subtitle">Koltuk: <strong>{pendingSeatId}</strong></p>
            <div className="ticket-type-options">
              {TICKET_TYPES.map(t => (
                <button
                  key={t.value}
                  className="ticket-type-option"
                  style={{ borderColor: t.color }}
                  onClick={() => handleTypeSelect(t.value)}
                >
                  <i className={`fa-solid ${t.icon}`} style={{ color: t.color }} />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
            <button className="cancel-btn" onClick={() => { setShowTypeModal(false); setPendingSeatId(null); }}>
              <i className="fa-solid fa-xmark" /> İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualReservation;
